/* eslint-disable jsdoc/no-undefined-types */

import { ensureGPAAInittedAndExec } from './frontend';

interface GPAAFieldsFilledPlace {
	geometry: {
		location: {
			lat: () => number;
			lng: () => number;
		};
	};
}

interface Coords {
	lat: number;
	lng: number;
}

interface GPAddressAutocompleteMapFieldOpts {
	formId: number;
	fieldId: number;
	addressFieldId: number;
	prepopulateCoords?: Coords;
	/** "address" plots a single marker; "travel_estimate" plots both endpoints and the route between them. */
	source?: 'address' | 'travel_estimate';
	travelEstimateFieldId?: number;
}

interface GPAATravelEstimateResult {
	polyline: string;
}

class GP_Address_Autocomplete_Map_Field {
	formId: number;

	fieldId: number;

	addressFieldId: number;

	prepopulateCoords?: Coords;

	source: 'address' | 'travel_estimate';

	travelEstimateFieldId?: number;

	route: google.maps.Polyline | undefined;

	routeMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

	mapContainer: HTMLElement;

	map: google.maps.Map | undefined;

	marker: google.maps.marker.AdvancedMarkerElement | undefined;

	zoom = 15;

	constructor({
		formId,
		fieldId,
		addressFieldId,
		prepopulateCoords,
		source,
		travelEstimateFieldId,
	}: GPAddressAutocompleteMapFieldOpts) {
		this.formId = formId;
		this.fieldId = fieldId;
		this.addressFieldId = addressFieldId;
		this.source =
			source === 'travel_estimate' ? 'travel_estimate' : 'address';
		this.travelEstimateFieldId = travelEstimateFieldId;
		this.prepopulateCoords =
			this.getPostedAddressFieldCoords() || prepopulateCoords;

		this.mapContainer = document.querySelector(
			`#gpaa_map_container_${this.fieldId}`
		)!;

		// The google maps sdk script will not be loaded unless the conncted address field has autocomplete enabled.
		// This is usually because the attached addressFieldId does not have the "Enable Google Address Autocomplete" option enabled.
		// We do check before rendering the map div if the autocomplete field is checked so this *shouldn't* happen, but just in case...
		if (window.google === undefined) {
			return;
		}

		this.setupListeners();

		// wait to init the map as the Google Maps API is loaded asynchronously
		ensureGPAAInittedAndExec(() => {
			this.initMap();
		});
	}

	// Get coords from an address field that is on a previous page of a multi-page form
	// These coords will be stored in a hidden input field
	getPostedAddressFieldCoords(): Coords | undefined {
		const $form = document.querySelector<HTMLFormElement>(
			`#gform_${this.formId}`
		);
		const $input = $form?.querySelector<HTMLInputElement>(
			`input[name="gpaa_coords_${this.addressFieldId}"]`
		);

		if (!$input?.value) {
			return;
		}

		let prepopulateCoords: Coords | undefined;

		try {
			prepopulateCoords = JSON.parse($input.value);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.debug(err);

			return;
		}

		// Convert lat and lng to numbers if they're strings.
		if (typeof prepopulateCoords?.lat === 'string') {
			prepopulateCoords.lat = Number(prepopulateCoords.lat);
		}

		if (typeof prepopulateCoords?.lng === 'string') {
			prepopulateCoords.lng = Number(prepopulateCoords.lng);
		}

		if (!prepopulateCoords?.lat || !prepopulateCoords?.lng) {
			return;
		}

		return prepopulateCoords;
	}

	setupListeners() {
		if (this.source === 'travel_estimate') {
			window.gform.addAction(
				'gpaa_travel_estimate_updated',
				(
					result: GPAATravelEstimateResult | null,
					formId: number,
					fieldId: number
				) => {
					if (
						formId !== this.formId ||
						fieldId !== this.travelEstimateFieldId
					) {
						return;
					}

					this.setRoute(result?.polyline || '');
				}
			);

			return;
		}

		window.gform.addAction(
			'gpaa_fields_filled',
			(
				place: GPAAFieldsFilledPlace,
				instance: null,
				formId: number,
				fieldId: number
			) => {
				if (formId !== this.formId || fieldId !== this.addressFieldId) {
					return;
				}

				this.setMarker({
					lat: place.geometry.location.lat(),
					lng: place.geometry.location.lng(),
				});
			}
		);
	}

	initMap() {
		let opts: google.maps.MapOptions = {
			zoom: this.zoom,
			center: {
				lng: -73.95,
				lat: 40.8,
			},
			fullscreenControl: false,
			mapTypeControl: false,
			streetViewControl: false,
			zoomControl: false,
			mapId: `gpaa_map_${this.formId}_${this.fieldId}`, // Map ID is required for advanced markers.
		};

		if (this.prepopulateCoords) {
			opts.center = this.prepopulateCoords;
		}

		/**
		 * Filter the options used to initialize the map.
		 *
		 * @param {google.maps.MapOptions} options Contains the map configuration options. All available options are documented here: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
		 * @param {number}                 formId  The current form ID.
		 * @param {number}                 fieldId The current field ID.
		 *
		 * @since 1.2.1
		 */
		opts = window.gform.applyFilters(
			'gpaa_map_options',
			opts,
			this.formId,
			this.fieldId
		);

		this.map = new google.maps.Map(this.mapContainer, opts);

		this.map?.setZoom(opts.zoom || this.zoom);

		if (this.prepopulateCoords && this.source === 'address') {
			this.setMarker(this.prepopulateCoords);
		}

		// The Travel Estimate field may have computed (or restored) a route before the map finished
		// initializing, in which case its action already fired. Pick the route up from its input.
		if (this.source === 'travel_estimate') {
			this.setRoute(
				document.querySelector<HTMLInputElement>(
					`#gform_${this.formId} input[name="gpaa_route_${this.travelEstimateFieldId}"]`
				)?.value || ''
			);
		}

		/**
		 * Action that fires once right after the map is initialized.
		 *
		 * @param {google.maps.Map} map     The map object.
		 * @param {number}          formId  The current form ID.
		 * @param {number}          fieldId The current field ID.
		 */
		window.gform.doAction(
			'gpaa_map_initialized',
			this.map,
			this.formId,
			this.fieldId
		);
	}

	/**
	 * Draw the route returned by the Travel Estimate field, with a marker at each end.
	 *
	 * @param encodedPolyline Google's encoded polyline for the route. Empty clears the map.
	 */
	setRoute(encodedPolyline: string) {
		this.route?.setMap(null);
		this.route = undefined;

		this.routeMarkers.forEach((marker) => {
			marker.map = null;
		});
		this.routeMarkers = [];

		if (!encodedPolyline || !this.map) {
			return;
		}

		const path = google.maps.geometry.encoding.decodePath(encodedPolyline);

		if (!path.length) {
			return;
		}

		let opts: google.maps.PolylineOptions = {
			path,
			map: this.map,
			strokeColor: '#4679aa',
			strokeOpacity: 0.9,
			strokeWeight: 5,
		};

		/**
		 * Filter the options used to draw the route on the map.
		 *
		 * @param {google.maps.PolylineOptions} options Contains the polyline options.
		 * @param {number}                      formId  The current form ID.
		 * @param {number}                      fieldId The current field ID.
		 *
		 * @since 2.0
		 */
		opts = window.gform.applyFilters(
			'gpaa_route_options',
			opts,
			this.formId,
			this.fieldId
		);

		this.route = new google.maps.Polyline(opts);

		const bounds = new google.maps.LatLngBounds();
		path.forEach((point) => bounds.extend(point));

		[path[0], path[path.length - 1]].forEach((position) => {
			this.routeMarkers.push(
				new google.maps.marker.AdvancedMarkerElement({
					position,
					map: this.map,
				})
			);
		});

		this.map.fitBounds(bounds);

		/**
		 * Action that fires after a route is drawn on the map.
		 *
		 * @param params.formId  ID of the form the map field is in.
		 * @param params.fieldId ID of the map field the route was drawn on.
		 * @param params.map     The map object.
		 * @param params.route   The polyline that was drawn.
		 *
		 * @since 2.0
		 */
		window.gform.doAction('gpaa_route_set', {
			formId: this.formId,
			fieldId: this.fieldId,
			map: this.map,
			route: this.route,
		});
	}

	setMarker(position: Coords) {
		// remove marker from the map if it exists
		if (this.marker) {
			this.marker.map = null;
		}

		let opts: google.maps.marker.AdvancedMarkerElementOptions = {
			position,
			map: this.map,
		};

		/**
		 * Filter the options used to add a marker to the map.
		 *
		 * @param {google.maps.MarkerOptions} options Contains the marker options. All available options are documented here: https://developers-dot-devsite-v2-prod.appspot.com/maps/documentation/javascript/reference/marker#MarkerOptions
		 * @param {number}                    formId  The current form ID.
		 * @param {number}                    fieldId The current field ID.
		 *
		 * @since 1.2.1
		 */
		opts = window.gform.applyFilters(
			'gpaa_marker_options',
			opts,
			this.formId,
			this.fieldId
		);

		this.marker = new google.maps.marker.AdvancedMarkerElement(opts);

		const markerPos = this.marker.position;

		if (markerPos) {
			this.map?.panTo(markerPos);
		}

		/**
		 * Action that fires after a marker is added to the map.
		 *
		 * @param params.formId         ID of the form the map field is in.
		 * @param params.fieldId        ID of the map field the marker was added to.
		 * @param params.addressFieldId ID of the address field the map field is attached to.
		 * @param params.map            The map object that the marker was added to.
		 * @param params.marker         The marker object that was addded to the map.
		 *
		 * @since 1.2.3
		 */
		window.gform.doAction('gpaa_marker_set', {
			formId: this.formId,
			fieldId: this.fieldId,
			addressFieldId: this.addressFieldId,
			map: this.map,
			marker: this.marker,
		});
	}
}

window.GP_Address_Autocomplete_Map_Field = GP_Address_Autocomplete_Map_Field;

// Make this a module to avoid TypeScript error with block-scoped variables since we're not importing anything
export {};
